import axios from 'axios';

// Add TypeScript interface for the window object with API_CONFIG
declare global {
    interface Window {
        API_CONFIG?: {
            BASE_URL: string;
        };
    }
}

// Get the API base URL from multiple sources
const getApiBaseUrl = (): string => {
  // 1. Check for runtime configuration injected into window object (useful for Cloud Run)
  if (window.API_CONFIG && window.API_CONFIG.BASE_URL) {
    console.log('Auth Service: Using window.API_CONFIG.BASE_URL:', window.API_CONFIG.BASE_URL);
    return window.API_CONFIG.BASE_URL;
  }
  
  // 2. Check for environment variable
  if (process.env.REACT_APP_API_BASE_URL) {
    console.log('Auth Service: Using REACT_APP_API_BASE_URL:', process.env.REACT_APP_API_BASE_URL);
    return process.env.REACT_APP_API_BASE_URL;
  }
  
  // 3. Handle different environments
  const isProduction = process.env.NODE_ENV === 'production';
  
  if (isProduction) {
    // In production, try to use the same origin if no specific URL was provided
    console.log('Auth Service: Using window.location.origin in production:', window.location.origin);
    return `${window.location.origin}/api`;
  }
  
  // 4. Fallback for development
  console.log('Auth Service: Using default localhost API URL');
  return 'http://localhost:5000/api';
};

const API_BASE_URL = getApiBaseUrl();
console.log('Auth Service: Final API_BASE_URL:', API_BASE_URL);

export interface LoginCredentials {
    email: string;
    password: string;
    rememberMe?: boolean;
}

export interface RegisterData extends Omit<LoginCredentials, 'rememberMe'> {
    name: string;
}

export interface User {
    email: string;
    name: string;
    is_admin: boolean;
}

export interface AuthResponse {
    token: string;
    user: User;
}

const authService = {
    async login(credentials: LoginCredentials): Promise<AuthResponse> {
        const response = await axios.post(`${API_BASE_URL}/auth/login`, credentials, {
            withCredentials: true
        });
        this.setAuthToken(response.data.token, credentials.rememberMe);
        return response.data;
    },

    async register(data: RegisterData): Promise<AuthResponse> {
        const response = await axios.post(`${API_BASE_URL}/auth/register`, data, {
            withCredentials: true
        });
        this.setAuthToken(response.data.token, true); // Always remember on register
        return response.data;
    },

    async getCurrentUser(): Promise<User> {
        const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
        if (!token) {
            throw new Error('No token found');
        }

        const response = await axios.get(`${API_BASE_URL}/auth/me`, {
            withCredentials: true,
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    },

    setAuthToken(token: string | null, remember: boolean = false) {
        if (token) {
            if (remember) {
                localStorage.setItem('auth_token', token);
            } else {
                sessionStorage.setItem('auth_token', token);
            }
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
            localStorage.removeItem('auth_token');
            sessionStorage.removeItem('auth_token');
            delete axios.defaults.headers.common['Authorization'];
        }
    },

    logout() {
        this.setAuthToken(null);
    }
};

export default authService; 